/**
 * @file Point d'entree de la config : GameConfig (objet injectable dans Simulation) + index de recherche.
 */
import type { BiomeId, GradeId, ModeId, WeaponBehavior } from '@shared/enums';
import { BALANCE } from './balance';
import { BIOME_DEFS } from './biomes';
import { FISH, PETS, RELICS } from './collectibles';
import { GRADE_DEFS } from './grades';
import { DAILY_REWARDS, MISSIONS, PASS } from './missions';
import { MODES } from './modes';
import { SIMULATION } from './simulation';
import { AMMO_SKINS } from './ammoSkins';
import { AURAS } from './auras';
import { FIREWORKS } from './fireworks';
import { SHOT_FX } from './shotFx';
import { SKINS } from './skins';
import { TARGETS } from './targets';
import { FEATURE_UNLOCKS } from './unlocks';
import { BEHAVIOR_PARAMS } from './weaponBehaviors';
import { WEAPONS } from './weapons';
import type {
  AmmoSkinDef,
  AuraDef,
  FireworkDef,
  ShotFxDef,
  BalanceConfig,
  BehaviorParams,
  BiomeDef,
  DailyRewardDay,
  FishDef,
  GradeDef,
  MissionDef,
  ModeDef,
  PassConfig,
  PetDef,
  RelicDef,
  SimulationConfig,
  SkinDef,
  TargetDef,
  WeaponDef,
} from './types';

export interface GameConfig {
  sim: SimulationConfig;
  balance: BalanceConfig;
  grades: readonly GradeDef[];
  weapons: readonly WeaponDef[];
  behaviors: Record<WeaponBehavior, BehaviorParams>;
  targets: readonly TargetDef[];
  biomes: Record<BiomeId, BiomeDef>;
  modes: { [K in ModeId]: ModeDef<K> };
  skins: readonly SkinDef[];
  ammoSkins: readonly AmmoSkinDef[];
  auras: readonly AuraDef[];
  fireworks: readonly FireworkDef[];
  shotFx: readonly ShotFxDef[];
  relics: readonly RelicDef[];
  pets: readonly PetDef[];
  fish: readonly FishDef[];
  missions: readonly MissionDef[];
  pass: PassConfig;
  dailyRewards: readonly DailyRewardDay[];
  featureUnlocks: typeof FEATURE_UNLOCKS;
}

/** Configuration par defaut du jeu. Les tests peuvent en produire une copie modifiee (structuredClone + override). */
export const CONFIG: GameConfig = {
  sim: SIMULATION,
  balance: BALANCE,
  grades: GRADE_DEFS,
  weapons: WEAPONS,
  behaviors: BEHAVIOR_PARAMS,
  targets: TARGETS,
  biomes: BIOME_DEFS,
  modes: MODES,
  skins: SKINS,
  ammoSkins: AMMO_SKINS,
  auras: AURAS,
  fireworks: FIREWORKS,
  shotFx: SHOT_FX,
  relics: RELICS,
  pets: PETS,
  fish: FISH,
  missions: MISSIONS,
  pass: PASS,
  dailyRewards: DAILY_REWARDS,
  featureUnlocks: FEATURE_UNLOCKS,
};

/** Index de recherche O(1) construits une fois a partir d'une GameConfig. */
export interface ConfigIndex {
  weapon(id: string): WeaponDef;
  weaponsOfGrade(grade: GradeId): readonly WeaponDef[];
  target(id: string): TargetDef;
  skin(id: string): SkinDef;
  ammoSkin(id: string): AmmoSkinDef;
  aura(id: string): AuraDef;
  firework(id: string): FireworkDef;
  shotFx(id: string): ShotFxDef;
  grade(id: GradeId): GradeDef;
  mission(id: string): MissionDef;
}

export const buildConfigIndex = (cfg: GameConfig): ConfigIndex => {
  const weapons = new Map(cfg.weapons.map((w) => [w.id, w]));
  const byGrade = new Map<GradeId, WeaponDef[]>();
  for (const w of cfg.weapons) {
    const l = byGrade.get(w.grade) ?? [];
    l.push(w);
    byGrade.set(w.grade, l);
  }
  const targets = new Map(cfg.targets.map((t) => [t.id, t]));
  const skins = new Map(cfg.skins.map((s) => [s.id, s]));
  const ammoSkins = new Map(cfg.ammoSkins.map((s) => [s.id, s]));
  const auras = new Map(cfg.auras.map((s) => [s.id, s]));
  const fireworks = new Map(cfg.fireworks.map((s) => [s.id, s]));
  const shotFxs = new Map(cfg.shotFx.map((s) => [s.id, s]));
  const grades = new Map(cfg.grades.map((g) => [g.id, g]));
  const missions = new Map(cfg.missions.map((m) => [m.id, m]));
  const need = <T>(m: Map<string, T>, kind: string, id: string): T => {
    const v = m.get(id);
    if (v === undefined) throw new Error(`Unknown ${kind}: ${id}`);
    return v;
  };
  return {
    weapon: (id) => need(weapons, 'weapon', id),
    weaponsOfGrade: (g) => byGrade.get(g) ?? [],
    target: (id) => need(targets, 'target', id),
    skin: (id) => need(skins, 'skin', id),
    ammoSkin: (id) => need(ammoSkins, 'ammoSkin', id),
    aura: (id) => need(auras, 'aura', id),
    firework: (id) => need(fireworks, 'firework', id),
    shotFx: (id) => need(shotFxs, 'shotFx', id),
    grade: (id) => need(grades as Map<string, GradeDef>, 'grade', id),
    mission: (id) => need(missions, 'mission', id),
  };
};

export * from './types';
export { TICK_DT } from './simulation';
export { CAT_ANIM, RENDER } from './render';
export { SFX_RECIPES } from './audio';
export { TUTORIAL_STEPS } from './unlocks';
export type { FeatureId, TutorialStepDef } from './unlocks';
export { DEFAULT_SKIN_ID } from './skins';
export { DEFAULT_AMMO_SKIN } from './ammoSkins';
export { DEFAULT_FIREWORK } from './fireworks';
export { FIREWORK_COOLDOWN, HUB_CATNIP, HUB_GIANT_SPOTS, HUB_PLACES, HUB_RADIUS, HUB_SEASONS, HUB_SEASON_IDS, HUB_ZOOM, seasonOf } from './hub';
export type { HubPlace, HubSeason, HubSeasonDef } from './hub';
export { STARTER_WEAPON_ID, STARTER_CHEST_WEAPON_IDS } from './weapons';
export { BIOME_ORDER } from './biomes';
